import type { SSEEvent } from "../types";

export type ParsedSSEMessage = {
	eventType: string;
	data: SSEEvent;
};

/**
 * Parses a raw SSE message string into structured event data.
 * Returns null if the message is incomplete or malformed.
 *
 * @param message - Raw SSE message string (event + data lines)
 * @returns Parsed SSE message or null if invalid
 */
export const parseSSEMessage = (message: string): ParsedSSEMessage | null => {
	if (!message.trim()) {
		return null;
	}

	const lines = message.split("\n");
	let eventType = "";
	let eventData = "";

	for (const line of lines) {
		if (line.startsWith("event:")) {
			eventType = line.substring(6).trim();
		} else if (line.startsWith("data:")) {
			eventData = line.substring(5).trim();
		}
	}

	if (!eventType || !eventData) {
		return null;
	}

	try {
		const parsed = JSON.parse(eventData) as SSEEvent;
		return { eventType, data: parsed };
	} catch {
		// Return null for malformed JSON
		return null;
	}
};

/**
 * Processes a buffer of SSE data and extracts complete messages.
 * SSE messages are separated by double newlines.
 *
 * @param buffer - Accumulated buffer of SSE data
 * @returns Object with array of complete messages and remaining buffer
 */
export const extractSSEMessages = (buffer: string): { messages: string[]; remainingBuffer: string } => {
	const parts = buffer.split("\n\n");
	const remainingBuffer = parts.pop() || "";
	return {
		messages: parts.filter((msg) => msg.trim().length > 0),
		remainingBuffer,
	};
};
